import { api } from "./api";
import type { LeadInput } from "./crmApi";

const base = (orgId: string) => `/org/${orgId}/crm/lead-generator`;

export type LeadGenProfile = {
  business_description: string;
  target_description: string;
  include_website: boolean;
  postcode: string;
  updated_at?: string | null;
};

export type LeadGenRun = {
  id: string;
  status: "queued" | "running" | "succeeded" | "failed";
  postcode: string;
  country: string;
  requested?: number;
  candidates?: number;
  created_count?: number;
  error_code?: string | null;
  error_message?: string | null;
  created_at: string;
  finished_at?: string | null;
};

export type LeadGenStatus = {
  /** The organization's country (Settings -> App). Empty until an admin sets it. */
  country: string;
  /** The organization's own website (Settings -> App, Company details). Empty until set. */
  website: string;
  monthly_limit: number;
  used_this_month: number;
  remaining: number;
  ai_enabled: boolean;
  can_configure: boolean;
  latest_run: LeadGenRun | null;
  profile: LeadGenProfile | null;
};

export type LeadGenResult = {
  id: string;
  run_id: string;
  name: string;
  category: string | null;
  address: string | null;
  postcode: string | null;
  phone: string;
  email: string;
  website: string | null;
  fit_score: number | null;
  fit_reason: string | null;
  call_opener?: string | null;
  status: "pending" | "valid" | "invalid";
  processed_by: string | null;
  processed_by_email?: string | null;
  processed_at: string | null;
  lead_id: string | null;
  created_at: string;
};

export type CompanyMatch = { id: string; legal_name: string; company_code?: string | null };

/** Org override of the platform Lead generator search limits. */
export type LeadGenSearchLimit = {
  key: string;
  label: string;
  help: string;
  min: number;
  max: number;
  platform: number;
  value: number;
  overridden: boolean;
};

export type LeadGenSearchLimits = {
  enabled: boolean;
  limits: LeadGenSearchLimit[];
};

export const leadGenApi = {
  status: (orgId: string) => api.get<LeadGenStatus>(`${base(orgId)}/status`),
  searchLimits: (orgId: string) => api.get<LeadGenSearchLimits>(`${base(orgId)}/limits`),
  saveSearchLimits: (orgId: string, body: Record<string, number> | { reset: true }) =>
    api.put<LeadGenSearchLimits>(`${base(orgId)}/limits`, body),
  pending: (orgId: string) => api.get<{ results: LeadGenResult[] }>(`${base(orgId)}/results`),
  history: (orgId: string) => api.get<{ results: LeadGenResult[] }>(`${base(orgId)}/history`),
  saveProfile: (orgId: string, body: LeadGenProfile) => api.put<LeadGenProfile>(`${base(orgId)}/profile`, body),
  rewrite: (orgId: string, body: LeadGenProfile) => api.post<LeadGenProfile>(`${base(orgId)}/rewrite`, body),
  generate: (orgId: string, body: LeadGenProfile & { count: number }) =>
    api.post<LeadGenRun>(`${base(orgId)}/runs`, body),
  run: (orgId: string, runId: string) => api.get<LeadGenRun>(`${base(orgId)}/runs/${runId}`),
  email: (orgId: string, resultId: string) =>
    api.post<{ subject: string; body: string }>(`${base(orgId)}/results/${resultId}/email`, {}),
  invalid: (orgId: string, resultId: string) => api.post<LeadGenResult>(`${base(orgId)}/results/${resultId}/invalid`, {}),
  companyMatch: (orgId: string, resultId: string) =>
    api.get<{ result: LeadGenResult; company: CompanyMatch | null }>(`${base(orgId)}/results/${resultId}/company-match`),
  convert: (orgId: string, resultId: string, body: LeadInput) =>
    api.post<{ lead: { id: string; company_id?: string | null }; result: LeadGenResult }>(
      `${base(orgId)}/results/${resultId}/convert`,
      body,
    ),
};
